import { ChevronDown, CircleHelp } from "lucide-react";
import { formatDate } from "~/lib/format";
import type { CompanyProfile } from "~/types/company";
import { OverviewCard } from "./card";

interface FaqEntry {
	question: string;
	answer: string;
}

/** Builds the data-driven FAQ — only questions whose answer data exists. */
export function buildCompanyFaq(company: CompanyProfile): FaqEntry[] {
	const entries: FaqEntry[] = [];
	const address = [company.street, company.city, company.zip].filter(Boolean).join(", ");
	const vat = company.vatRegistration;
	const dic = vat?.dic ?? company.dic;

	if (address) {
		entries.push({
			question: `Kde sídlí ${company.name}?`,
			answer: `Společnost ${company.name} sídlí na adrese ${address}.`,
		});
	}

	entries.push({
		question: `Jaké má ${company.name} IČO${dic ? " a DIČ" : ""}?`,
		answer: dic
			? `IČO společnosti ${company.name} je ${company.ico}, DIČ je ${dic}.`
			: `IČO společnosti ${company.name} je ${company.ico}.`,
	});

	if (company.foundingDate) {
		entries.push({
			question: `Kdy vznikla ${company.name}?`,
			answer: `Společnost ${company.name} vznikla ${formatDate(company.foundingDate)}.`,
		});
	}

	if (vat) {
		entries.push({
			question: `Je ${company.name} plátcem DPH?`,
			answer: vat.isVatPayer
				? `Ano, společnost ${company.name} je plátcem DPH (DIČ ${vat.dic})${
						vat.registeredAt ? ` od ${formatDate(vat.registeredAt)}` : ""
					}.${vat.isUnreliable ? " Pozor — je vedena jako nespolehlivý plátce DPH." : ""}`
				: `Ne, společnost ${company.name} není plátcem DPH.`,
		});
	}

	return entries;
}

/** ⑪ FAQ — visible accordion backed by FAQPage JSON-LD. */
export function CompanyFaq({ company }: { company: CompanyProfile }) {
	const entries = buildCompanyFaq(company);
	if (entries.length === 0) return null;

	const jsonLd = {
		"@context": "https://schema.org",
		"@type": "FAQPage",
		mainEntity: entries.map((entry) => ({
			"@type": "Question",
			name: entry.question,
			acceptedAnswer: { "@type": "Answer", text: entry.answer },
		})),
	};

	return (
		<OverviewCard icon={CircleHelp} title="Časté otázky">
			<script
				type="application/ld+json"
				// biome-ignore lint/security/noDangerouslySetInnerHtml: JSON-LD structured data requires dangerouslySetInnerHTML
				dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
			/>
			<div className="flex flex-col gap-2">
				{entries.map((entry) => (
					<details
						key={entry.question}
						className="group rounded-lg border border-gray-100 px-4 py-3"
					>
						<summary className="flex cursor-pointer list-none items-center justify-between gap-2 text-sm font-medium [&::-webkit-details-marker]:hidden">
							{entry.question}
							<ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
						</summary>
						<p className="mt-2 text-sm text-muted-foreground">{entry.answer}</p>
					</details>
				))}
			</div>
		</OverviewCard>
	);
}
